import type { Clip } from '@/types'

/**
 * Whether an audio edit (gain / fades) may be written to a clip right now.
 *
 * Reads the LIVE clip and resolves its CURRENT owning track — a collaborator may
 * have moved the clip to another track, locked that track, or deleted the clip
 * between the user's gesture and the commit — so callers must never trust the
 * clip/track they captured when the gesture started. Every clip-audio write in
 * the app goes through `updateClipAudio` in the store, which applies this
 * against the Yjs maps themselves, inside the write transaction: the Zustand
 * projection is synced a tick behind the document, so a remote lock or move
 * that already reached Yjs would not be visible there yet.
 *
 * Accepts either the projection's `Map`s or the document's `Y.Map`s.
 */
export interface ClipAudioEditLookup<T> {
  get(key: string): T | undefined
  values(): Iterable<T>
}

export function canCommitClipAudioEdit(
  state: {
    clips: ClipAudioEditLookup<{ sequenceId: string; trackIndex: number }>
    tracks: ClipAudioEditLookup<{ sequenceId: string; index: number; locked?: boolean; type?: string }>
  },
  clipId: string,
): boolean {
  const liveClip = state.clips.get(clipId)
  if (!liveClip) return false
  for (const track of state.tracks.values()) {
    if (track.sequenceId === liveClip.sequenceId && track.index === liveClip.trackIndex)
      return !track.locked && track.type !== 'video'
  }
  return false
}

/** A gesture's optimistic precondition, checked against live Yjs before writing. */
export function timelineClipEditSnapshot(clip: Clip): string {
  return JSON.stringify([
    clip.id,
    clip.assetId,
    clip.sequenceId,
    clip.trackIndex,
    clip.startOnTimeline,
    clip.duration,
    clip.trimIn,
    clip.trimOut,
    clip.speed,
    clip.loop,
    clip.preservePitch,
    clip.gainDb,
    clip.fadeIn,
    clip.fadeOut,
    clip.volumePoints,
    clip.linkGroupId,
    clip.nestedSequenceId,
  ])
}
